import { Controller } from "@hotwired/stimulus"

/**
 * IoT Compare Toggle Controller
 *
 * Powers the reports filter bar's "Add comparison period" affordance
 * (packs/iot/app/views/iot/admin/reports/_filter_bar.html.erb): comparison
 * periods stay hidden until the operator explicitly adds one, each added
 * period carries its own remove (×), and the Add button disappears once every
 * slot the report allows is in use.
 *
 * Two form fields carry the state to the backend, and this controller only
 * ever drives them — it never renders or removes DOM nodes, so every slot the
 * report allows is already in the markup:
 *   - `compare` — a real checkbox, visually hidden via `sr-only` (not
 *     display:none, which would drop it from FormData), checked whenever at
 *     least one period is active. Keeps submitting compare=1 exactly as before
 *     CONEK-6899.
 *   - `compare_periods` — how many periods after Period A are active.
 *
 * Removing a period in the MIDDLE shifts every later period's values up so the
 * active slots stay contiguous. The backend reads the FIRST `compare_periods`
 * slots, so leaving a hole would silently drop whichever period sat after it.
 *
 * `hidden` is the native HTML attribute (not a Tailwind class): a period must
 * drop out of layout when hidden, unlike report_export_controller.js's label
 * swap, which only needs a visibility toggle within one grid cell.
 *
 * MUTUAL EXCLUSIVITY with location comparison: both toggle controllers mount
 * on the same wrapper, and each dispatches `<identifier>:changed` (detail:
 * {active}) whenever its mode flips. The wrapper's data-action routes each
 * event to the OTHER controller's onPeerChanged, which hides its own Add
 * button while the peer mode is active. Best-effort at this layer — the
 * authoritative rule lives server-side: period comparison deterministically
 * wins when both modes submit.
 */
export default class extends Controller {
  static targets = ["checkbox", "count", "period", "addButton"]

  connect() {
    // Seed from the peer's server-rendered checkbox: the first-connecting
    // controller's dispatch fires before the second is listening (Stimulus
    // installs data-action listeners at connect), and the flip-only guard
    // never re-announces — so an assumed-false seed would clobber the
    // server-rendered hidden on the Add button. The DOM is authoritative
    // at first paint.
    this.peerActive =
      this.element.querySelector(
        "[data-iot--location-compare-toggle-target~='checkbox']",
      )?.checked === true
    this.sync()
  }

  onPeerChanged(event) {
    this.peerActive = event.detail.active
    this.sync()
  }

  add() {
    const next = this.periodTargets.find((period) => period.hidden)
    if (!next) return

    next.hidden = false
    this.sync()
  }

  remove(event) {
    const row = event.target.closest(
      "[data-iot--compare-toggle-target='period']",
    )
    const index = this.periodTargets.indexOf(row)
    if (index < 0) return

    const activeCount = this.activePeriods().length
    for (let i = index; i < activeCount - 1; i++) {
      this.copyValues(this.periodTargets[i + 1], this.periodTargets[i])
    }

    const last = this.periodTargets[activeCount - 1]
    this.clearValues(last)
    last.hidden = true
    this.sync()
  }

  sync() {
    const activeCount = this.activePeriods().length

    this.checkboxTarget.checked = activeCount > 0
    // Never below 1: the value only matters once `compare` is checked, and a 0
    // would make the next Apply submit a comparison with no second period.
    this.countTarget.value = String(Math.max(activeCount, 1))

    if (this.hasAddButtonTarget) {
      this.addButtonTarget.hidden =
        activeCount >= this.periodTargets.length || this.peerActive === true
    }
    // Only announce real state FLIPS: the peer's onPeerChanged re-syncs (and
    // re-dispatches) — an unconditional dispatch here would ping-pong the two
    // controllers into an infinite synchronous event loop.
    const active = activeCount > 0
    if (this.lastDispatchedActive !== active) {
      this.lastDispatchedActive = active
      this.dispatch("changed", { detail: { active } })
    }
  }

  activePeriods() {
    return this.periodTargets.filter((period) => !period.hidden)
  }

  copyValues(from, to) {
    const sources = from.querySelectorAll("input")
    const targets = to.querySelectorAll("input")
    targets.forEach((input, i) => {
      input.value = sources[i] ? sources[i].value : ""
    })
    this.resyncDatePickers(to)
  }

  clearValues(row) {
    row.querySelectorAll("input").forEach((input) => {
      input.value = ""
    })
    this.resyncDatePickers(row)
  }

  /**
   * CONEK-7237. Each period date is a Ui::DatePickerComponent, which is TWO
   * inputs (a hidden ISO field and the visible localized text) plus Stimulus
   * state the assignments above cannot reach — so a shifted or cleared slot
   * would keep the old month highlighted in its calendar, and the next pick
   * would resume from a date the operator no longer sees.
   *
   * Dispatching `input` on the visible field runs ui--date-picker#onInput,
   * which re-parses the text, re-syncs the hidden ISO field and re-renders.
   * Its empty-string branch covers the cleared case explicitly.
   */
  resyncDatePickers(row) {
    row
      .querySelectorAll("[data-ui--date-picker-target='input']")
      .forEach((input) => {
        input.dispatchEvent(new Event("input", { bubbles: true }))
      })
  }
}
